package org.braunequip.web.components.bulma

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowRight
import kotlinx.coroutines.CoroutineScope
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.varabyte.kobweb.silk.components.forms.Button as onClickButton

data class GalleryImage(
    val url: String,
    val title: String? = null,
    val description: String? = null,
    val photoGalleryIndex: Int? = null
)


/*
     Goal: Make this PhotoGallery object something you can initialize with certain photos.
     - Can call function to retrieve the photos with index.
     - Clickable retrievable galleryImg objects that open gallery and take you to index.

     TODO: Fix the weird opacity/blur issue on the borders of background.
     TODO: getExternalImages() can remember which ones sent from before
        - This could be how to implement lazyLoading (ex. load 3, display show more button, get next 3).

 */
class PhotoGallery(private var id: String, imgUrls: List<String>) {
    private var _galleryState = mutableStateOf(ModalState())
    val galleryState: State<ModalState> = _galleryState

    private val _pictures = mutableStateOf(imgUrls.mapIndexed { index, url -> GalleryImage(url = url, photoGalleryIndex = index) })
    val pictures: State<List<GalleryImage>> = _pictures  // Expose as read-only

    private val _currIndex = mutableStateOf(0)
    val currIndex: State<Int> = _currIndex  // Expose as read-only


    fun openGallery(scope: CoroutineScope, startIndex: Int = 0) {
        _galleryState.value = galleryState.value.copy(isVisible = true, startIndex = startIndex)
        _currIndex.value = startIndex
    }

    private fun closeGallery() {
        _galleryState.value = galleryState.value.copy(isVisible = false)
    }

    private fun incrementIndex() {
        if (pictures.value.isNotEmpty()) {
            _currIndex.value = if (currIndex.value + 1 >= pictures.value.size) 0 else currIndex.value + 1
        }
    }

    private fun getNextIndex(): Int {
        if (pictures.value.isNotEmpty()) {
            return if (currIndex.value + 1 >= pictures.value.size) 0 else currIndex.value + 1
        } else {
            return 0
        }
    }

    private fun decrementIndex() {
        if (pictures.value.isNotEmpty()) {
            _currIndex.value = if (currIndex.value == 0) pictures.value.size - 1 else currIndex.value - 1
        }
    }

    private fun getPreviousIndex(): Int {
        if (pictures.value.isNotEmpty()) {
            return if (currIndex.value == 0) pictures.value.size - 1 else currIndex.value - 1
        } else {
            return 0
        }
    }

    @Composable
    fun DisplayGallery() {
        val state = galleryState.value

        if (state.isVisible) {
            Div(Modifier
                .id(id)
                .classNames("modal", "is-active")
                .toAttrs()
            ) {

                Div(Modifier
                    .background(
                        Background.of(
                            image = BackgroundImage.of(
                                url(pictures.value[currIndex.value].url)
                            ),
                            repeat = BackgroundRepeat.NoRepeat,
                            position = BackgroundPosition.of(CSSPosition.Center),
                            size = BackgroundSize.Cover
                        ),
                        Background.of(
                            image = linearGradient(LinearGradient.Direction.ToTop) {
                                add(rgba(0,0,0, 0.5), 100.percent)
                                add(Colors.Black, 100.percent)
                            }.toImage()
                        )
                    )
                    .filter(blur(10.px))
                    .border(10.px, LineStyle.Solid, Colors.Black)
                    .margin((-30).px)
                    .classNames("modal-background")
                    .onClick { closeGallery() }
                    .toAttrs()
                ) {}

                Div(Modifier
                    .width(100.percent)
                    .height(100.percent)
                    .classNames("modal-content")
                    .toAttrs()
                ) {

                    Column(Modifier
                        .width(100.percent)
                        .height(100.percent)
                        .overflow(Overflow.Hidden)
                    ) {
                        Row(Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.Center) {
                            P(Modifier.margin(0.px).padding(0.px).fontSize(28.px).color(Colors.White).toAttrs()) {
                                Text("${currIndex.value + 1} / ${pictures.value.size}")
                            }
                        }

                        Row(Modifier
                            .fillMaxWidth()
                            .fillMaxHeight()
                        ) {

                            /* Decrement Button */
                            Column(Modifier
                                .width(20.percent)
                                .fillMaxHeight()
                                .onClick {
                                    closeGallery()
                                },
                                verticalArrangement = Arrangement.Center,
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                onClickButton(
                                    modifier = Modifier,
                                    onClick = { decrementIndex() },
                                    colorPalette = null
                                ) { FaArrowLeft() }
                            }

                            Column(Modifier
                                .width(60.percent)
                                .fillMaxHeight(),
                                verticalArrangement = Arrangement.Center,
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                Img(
                                    src = pictures.value[currIndex.value].url,
                                    attrs = Modifier
                                        .maxWidth(autoLength)
                                        .maxHeight(100.percent)
                                        .objectFit(ObjectFit.Contain)
                                        .toAttrs()
                                )
                                Img(
                                    src = pictures.value[getNextIndex()].url,
                                    attrs = Modifier
                                        .display(DisplayStyle.None)
                                        .toAttrs()
                                )
                                Img(
                                    src = pictures.value[getPreviousIndex()].url,
                                    attrs = Modifier
                                        .display(DisplayStyle.None)
                                        .toAttrs()
                                )
                            }

                            /* Increment Button */
                            Column(Modifier
                                .width(20.percent)
                                .fillMaxHeight()
                                .onClick {
                                    closeGallery()
                                },
                                verticalArrangement = Arrangement.Center,
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                onClickButton(
                                    modifier = Modifier,
                                    onClick = { incrementIndex() },
                                    colorPalette = null
                                ) { FaArrowRight() }
                            }

                        }
                    }
                }

                Button(Modifier
                    .classNames("modal-close", "is-large")
                    .ariaLabel("close")
                    .onClick {
                        closeGallery()
                    }
                    .toAttrs()) {}
            }
        }
    }

    fun getExternalImages(amount: Int): List<GalleryImage> {
        return pictures.value.take(amount)
    }

    // Data class to hold modal state
    data class ModalState(
        val isVisible: Boolean = false,
        val startIndex: Int = 0 // To track which image to display first
    )
}